import React, { useState } from 'react';
import './RecentPostsCarousel.scss';
import { useSelector } from 'react-redux';
import { Card } from 'react-bootstrap';
import Carousel from 'react-simply-carousel';
import { createdMonthDayYear } from '../../../commonservice';
import cn from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addPageAction } from '../../../Redux/slices/pageDetailSlice';
import { RouteConstants } from '../../../Constants/routeConstants';
import PostCarouselPopup from '../../Modals/PostCarouselPopup/PostCarouselPopup';

interface RecentPostsCarouselProps {
  userData: any;
  pageData: any;
  canAction: () => boolean;
  stopAllMedia: (key: any, vindex: any, type: string) => void;
}

const RecentPostsCarousel: React.FC<RecentPostsCarouselProps> = ({
  userData,
  pageData,
  canAction,
  stopAllMedia,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeSlide, setActiveSlide] = useState(0);
  const [selectedPost, setSelectedPost] = useState<any>(null);
  const [showPopup, setShowPopup] = useState(false);
  const posts = useSelector((state: any) => state?.post?.postList?.data || []);

  const recentPosts = React.useMemo(() => {
    if (!Array.isArray(posts)) return [];

    return posts
      .filter((post: any) => !post.isPinned)
      .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 10);
  }, [posts]);

  const loginRedirect = () => {
    navigate(RouteConstants.LOGIN);
  };

  const handlePostClick = (post: any) => {
    setSelectedPost(post);
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedPost(null);
  };

  const responsive = [
    { minWidth: 1024.9, itemsToShow: 3 },
    { minWidth: 768, itemsToShow: 2 },
    { maxWidth: 767, itemsToShow: 1 },
  ];

  if (!Array.isArray(recentPosts) || recentPosts.length === 0) {
    console.log('No recent posts available to display in carousel');
    return null;
  }

  const renderPostCard = (post: any, index: number) => {
    return (
      <Card className="PostCard">
        <div className="Header">
          <div className="Profile">
            <div className="owner">
              <div className="ownerPic">
                {post?.createdBy?.image ? (
                  <img src={post.createdBy.image} alt="" />
                ) : (
                  <div className="ownerAlpha">
                    <span>{post?.createdBy?.name?.charAt(0)}</span>
                  </div>
                )}
              </div>
              <div className="ownerName">{post?.createdBy?.name}</div>
            </div>
          </div>
        </div>

        <div className="Body">
          {post?.medias?.length > 0 && (
            <div className="GridWrapper">
              <div className="ImageGrid">
                <div className={cn('grid-container', {
                  'single-image': post?.medias.length === 1,
                  'two-image-grid': post?.medias.length === 2,
                  'three-image-grid': post?.medias.length === 3,
                  'four-image-grid': post?.medias.length === 4,
                })}>
                  {post?.medias?.map((media: any, mediaIndex: number) => {
                    if (mediaIndex < 4) {
                      return (
                        <div
                          key={mediaIndex}
                          className={cn(`eachThumbnail eachThumbnail-${mediaIndex + 1}`, {
                            'video-type': media.type === 'video',
                            'no-video-thumb': media.type === 'video' && !media.thumbnail,
                          })}
                          style={{
                            backgroundImage: `url(${media.type === 'video' && media.thumbnail ? media.thumbnail : media.url})`
                          }}
                        >
                          {media.type === 'video' && (
                            <div className="play-icon">
                              <i className="fa fa-play"></i>
                            </div>
                          )}
                          {post?.medias.length > 4 && mediaIndex === 3 && (
                            <div className="more-media-overlay">
                              <span>+{post.medias.length - 4}</span>
                            </div>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            </div>
          )}

          <div className="post-content">
            <div className="post-title">
              {post?.title || post?.caption || 'Untitled Post'}
            </div>
            <div className="post-preview">
              {post?.description?.substring(0, 150)}...
            </div>
            <div className="post-meta">
              <span className="post-date">
                <i className="fa fa-calendar"></i>
                {createdMonthDayYear(post?.createdAt)}
              </span>
              <button 
                className="read-more-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePostClick(post);
                }}
              >
                View Post
              </button>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="recent-posts-carousel">
      <h4>Recent Posts</h4>
      <Carousel
        containerProps={{
          style: {
            width: '100%',
            justifyContent: 'space-between',
            userSelect: 'text',
          },
          className: 'carousel-wrapper',
        }}
        infinite={false}
        activeSlideIndex={activeSlide}
        onRequestChange={setActiveSlide}
        forwardBtnProps={{
          children: <i className="fa fa-arrow-right"></i>,
          className: 'control next',
        }}
        backwardBtnProps={{
          children: <i className="fa fa-arrow-left"></i>,
          className: 'control prev',
        }}
        dotsNav={{
          show: false,
        }}
        speed={100}
        responsiveProps={responsive}
        preventScrollOnSwipe={true}
      >
        {recentPosts.map((post: any, index: number) => (
          <div
            key={index}
            className="post-item"
            onClick={() => handlePostClick(post)}
          >
            {renderPostCard(post, index)}
          </div>
        ))}
      </Carousel>

      <PostCarouselPopup
        show={showPopup}
        handleClose={handleClosePopup}
        post={selectedPost}
      />
    </div>
  );
};

export default RecentPostsCarousel; 